'use client';

import { useTranslations } from 'next-intl';
import { Brain } from 'lucide-react';
import { SettingsItem } from '@/shared/ui/settings';
import { ProBadge } from '@/features/subscription';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { usePaywallPayload } from '@/features/subscription/model/subscription.selectors';

interface ExtendedThinkingSectionProps {
  isExtendedThinking: boolean;
  onIsExtendedThinkingChange: (value: boolean) => void;
  /** Advanced engine control — locked (Pro Pass upsell) when the building lacks
   *  `has_advanced_render_controls`. Locked → the row shows the PRO badge and a
   *  tap opens the Pro Pass paywall instead of toggling. Mirrors Apple/Android. */
  locked?: boolean;
}

/**
 * Extended thinking toggle — an advanced engine control. Always visible; when
 * the building lacks `has_advanced_render_controls` it renders LOCKED with a PRO
 * badge and a tap opens the Pro Pass paywall (no toggle). Unlocked (Pro Pass /
 * unlimited building) it's an interactive switch. Mirrors the iOS Engine row.
 */
export function ExtendedThinkingSection({
  isExtendedThinking,
  onIsExtendedThinkingChange,
  locked = false,
}: ExtendedThinkingSectionProps) {
  const t = useTranslations('render.settings.extendedThinking');
  const showLimitPaywall = useLimitPaywallStore((s) => s.showPaywall);
  const showPackPaywall = useLimitPaywallStore((s) => s.showPackPaywall);
  // Server-driven "unlock Extended thinking" payload (Pro Pass / Facade Kit /
  // Pro Plan tiles) — see ModelContent.tsx for the full rationale.
  const extendedThinkingPaywall = usePaywallPayload('PRO_FEATURE_UNAVAILABLE');

  if (locked) {
    return (
      <SettingsItem
        icon={Brain}
        title={t('title')}
        paddingClassName="px-4 py-3"
        titleAdornment={<ProBadge />}
        onClick={() => {
          // Until the cached plan payload has loaded (rare — the plan is
          // fetched well before the user reaches this control) open the Kit
          // sheet directly; it loads its own copy from GET /store. Parity with
          // Apple's `showFeaturePaywall` and Android's `FeaturePaywallViewModel`
          // fallback — a locked knob must never be a dead tap.
          if (extendedThinkingPaywall) {
            showLimitPaywall(extendedThinkingPaywall, 'PRO_FEATURE_UNAVAILABLE');
          } else {
            showPackPaywall();
          }
        }}
      />
    );
  }

  return (
    <SettingsItem
      icon={Brain}
      title={t('title')}
      paddingClassName="px-4 py-3"
      toggle={{
        checked: isExtendedThinking,
        onCheckedChange: onIsExtendedThinkingChange,
      }}
      rowTogglesSwitch
    />
  );
}
